import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Video, Film, Upload, Wand2, Play, Download, Trash2,
  Loader, Save, RefreshCw, FileText, Image as ImageIcon,
  Edit3, Check, X, Send, MessageSquare, Sparkles, Info, HelpCircle, BookOpen, AlertTriangle, XCircle,
  Monitor, Smartphone, Clock, Zap
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import { getConfig } from '../../../../config/production';
import { InfoModal } from '../../../common/InfoModal';
import { modelServiceFetch, modelServicePost, modelServiceUpload } from '../../../../utils/modelService';
const API_URL_BASE = getConfig('API_URL') || 'https://api.jpro.app/api/v1';
const cleanUrl = API_URL_BASE.endsWith('/') ? API_URL_BASE.slice(0, -1) : API_URL_BASE;
const API_URL = cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`;

const VIDEO_STYLES = [
  { id: 'educational', name: 'Educational', description: 'Professional and instructional' },
  { id: 'cinematic', name: 'Cinematic', description: 'Dramatic and visually stunning' },
  { id: 'documentary', name: 'Documentary', description: 'Realistic and informative' },
  { id: 'animated', name: 'Animated', description: 'Cartoon-style animation' }
];

const RESOLUTIONS = [
  { id: '720p', name: '720p', description: 'Standard HD' },
  { id: '1080p', name: '1080p', description: 'Full HD' },
  { id: '4k', name: '4K', description: 'Ultra HD' }
];



function VideoGeneration() {
  const { theme } = useTheme();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const refImageInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Input states
  const [topic, setTopic] = useState('');
  const [useRAG, setUseRAG] = useState(true);
  const [script, setScript] = useState('');
  const [scenes, setScenes] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [userPrompt, setUserPrompt] = useState('');

  // File uploads
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [referenceImages, setReferenceImages] = useState([]); // max 3, for Veo style guidance

  // Video config
  const [style, setStyle] = useState('educational');
  const [targetDuration, setTargetDuration] = useState(30); // seconds
  const [resolution, setResolution] = useState('720p');
  const [aspectRatio, setAspectRatio] = useState('16:9');

  // UI states
  const [showInfo, setShowInfo] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [currentStep, setCurrentStep] = useState('input');
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [error, setError] = useState('');

  // Async polling
  const [taskId, setTaskId] = useState(null);
  const [extensionProgress, setExtensionProgress] = useState(null);

  // Compute optimal scenes + duration per scene from target duration
  // Prefer 8s scenes, fall back to 6s or 4s for precise fit
  const computeScenePlan = (target) => {
    const clamped = Math.min(Math.max(target, 4), 148);
    // Try 8s first, then 6s, then 4s — pick whichever gives closest fit
    const options = [8, 6, 4].map(d => {
      const scenes = Math.max(1, Math.round(clamped / d));
      return { duration: String(d), scenes, total: scenes * d, diff: Math.abs(scenes * d - clamped) };
    });
    options.sort((a, b) => a.diff - b.diff);
    return options[0];
  };

  const scenePlan = computeScenePlan(targetDuration);
  const maxScenes = scenePlan.scenes;
  const durationPerScene = scenePlan.duration;
  const totalDuration = scenePlan.total;
  const effectiveResolution = maxScenes > 1 ? '720p' : resolution;

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // ---- Polling ----

  const startPolling = useCallback((tid) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await modelServiceFetch(`/video/generate/status/${tid}`);
        if (!res.ok) throw new Error('Failed to get status');

        const data = await res.json();

        if (data.status === 'generating' || data.status === 'extending') {
          setExtensionProgress({
            status: data.status,
            currentSeconds: data.current_seconds,
            targetSeconds: data.target_seconds,
            currentScene: data.current_scene,
            totalScenes: data.total_scenes,
            resolution: data.resolution,
          });
        } else if (data.status === 'done') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;

          const videoBlob = base64ToBlob(data.result.video_base64, 'video/mp4');
          const videoUrl = URL.createObjectURL(videoBlob);

          setGeneratedVideo({
            url: videoUrl,
            blob: videoBlob,
            duration: data.result.duration,
            resolution: data.result.resolution,
            aspect_ratio: data.result.aspect_ratio,
            scenes_count: data.result.total_scenes,
            size_bytes: data.result.size_bytes,
          });

          setCurrentStep('preview');
          setIsGenerating(false);
          setTaskId(null);
          setExtensionProgress(null);
        } else if (data.status === 'error') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setError(data.error || 'Video generation failed');
          setCurrentStep('input');
          setIsGenerating(false);
          setTaskId(null);
          setExtensionProgress(null);
        }
      } catch (err) {
        // Don't stop polling on transient errors
        console.error('Polling error:', err);
      }
    }, 3000);
  }, []);

  // ---- Handlers ----

  const handleAIChat = async () => {
    if (!userPrompt.trim()) return;

    setIsChatting(true);
    setError('');

    const userMessage = { role: 'user', content: userPrompt };
    const currentPrompt = userPrompt;
    setChatMessages(prev => [...prev, userMessage]);
    setUserPrompt('');

    try {
      const documentFiles = uploadedFiles.filter(f => f.type === 'document');
      let materialsText = '';
      for (const doc of documentFiles) {
        materialsText += `\n\n--- ${doc.name} (${doc.contentType}) ---\n[File content available as base64]\n`;
      }

      const context = chatMessages.map(m => `${m.role}: ${m.content}`).join('\n');

      const response = await modelServicePost('/video/assist-script', {
        user_prompt: currentPrompt,
        chat_history: context,
        current_script: script,
        materials_text: materialsText,
        use_rag: useRAG,
        style
      });

      if (!response.ok) throw new Error('Failed to get AI response');

      const data = await response.json();

      const aiMessage = { role: 'assistant', content: data.response };
      setChatMessages(prev => [...prev, aiMessage]);

      if (data.script) {
        setScript(data.script);
      }
    } catch (err) {
      setError(err.message);
      setChatMessages(prev => prev.slice(0, -1));
      setUserPrompt(currentPrompt);
    } finally {
      setIsChatting(false);
    }
  };

  const handleEnhancePrompt = async () => {
    if (!topic.trim()) return;

    setIsEnhancing(true);
    setError('');

    try {
      const response = await modelServicePost('/video/enhance-prompt', {
        raw_description: topic,
        style
      });

      if (!response.ok) throw new Error('Failed to enhance prompt');

      const data = await response.json();
      setTopic(data.enhanced_prompt);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleGenerateScript = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const documentFiles = uploadedFiles.filter(f => f.type === 'document');
      let materialsText = '';
      for (const doc of documentFiles) {
        materialsText += `\n\n--- ${doc.name} (${doc.contentType}) ---\n[File content available]\n`;
      }

      const response = await modelServicePost('/video/generate-script', {
        topic,
        materials_text: materialsText,
        use_rag: useRAG,
        style
      });

      if (!response.ok) throw new Error('Failed to generate script');

      const data = await response.json();
      setScript(data.script);
      setCurrentStep('script');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickGenerateVideo = async () => {
    setIsGenerating(true);
    setError('');
    setCurrentStep('generating');
    setExtensionProgress(null);

    try {
      const prompt = userPrompt.trim() || topic || script;
      if (!prompt) throw new Error('Please provide a prompt or topic');

      // Combine reference images + any images from materials (max 3 for Veo)
      const allRefImages = [
        ...referenceImages.map(img => img.content),
        ...uploadedFiles.filter(f => f.type === 'image').map(f => f.content),
      ].slice(0, 3);

      const response = await modelServicePost('/video/quick-generate', {
        prompt,
        reference_images: allRefImages,
        use_rag: useRAG,
        style,
        resolution: effectiveResolution,
        aspect_ratio: aspectRatio,
        duration_per_scene: durationPerScene,
        num_scenes: maxScenes,
      });

      if (!response.ok) throw new Error('Failed to start video generation');

      const data = await response.json();
      setTaskId(data.task_id);
      startPolling(data.task_id);
    } catch (err) {
      setError(err.message);
      setCurrentStep('input');
      setIsGenerating(false);
    }
  };

  const handleParseScenes = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const response = await modelServicePost('/video/parse-scenes', {
        script,
        max_scenes: maxScenes
      });

      if (!response.ok) throw new Error('Failed to parse scenes');

      const data = await response.json();
      setScenes(data.scenes);
      setCurrentStep('scenes');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdateScene = (index, field, value) => {
    const updatedScenes = [...scenes];
    updatedScenes[index][field] = value;
    setScenes(updatedScenes);
  };

  const handleGenerateVideo = async () => {
    setIsGenerating(true);
    setError('');
    setCurrentStep('generating');
    setExtensionProgress(null);

    try {
      // Combine reference images + any images from materials (max 3 for Veo)
      const allRefImages = [
        ...referenceImages.map(img => img.content),
        ...uploadedFiles.filter(f => f.type === 'image').map(f => f.content),
      ].slice(0, 3);

      const response = await modelServicePost('/video/generate', {
        style,
        script,
        scenes,
        reference_images: allRefImages,
        resolution: effectiveResolution,
        aspect_ratio: aspectRatio,
        duration_per_scene: durationPerScene,
      });

      if (!response.ok) throw new Error('Failed to start video generation');

      const data = await response.json();
      setTaskId(data.task_id);
      startPolling(data.task_id);
    } catch (err) {
      setError(err.message);
      setCurrentStep('scenes');
      setIsGenerating(false);
    }
  };

  const handleSaveVideo = async () => {
    if (!generatedVideo) return;

    setIsGenerating(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('video_file', generatedVideo.blob, 'generated_video.mp4');
      formData.append('title', topic || 'Generated Video');
      formData.append('description', `Generated ${generatedVideo.scenes_count}-scene video (${generatedVideo.resolution})`);
      formData.append('style', style);
      formData.append('duration', generatedVideo.duration);
      formData.append('scenes_count', generatedVideo.scenes_count);
      formData.append('metadata', JSON.stringify({ script, scenes }));

      const { apiUpload } = await import('../../../../utils/api');
      const response = await apiUpload('/videos', formData);

      if (!response.ok) throw new Error('Failed to save video');

      alert('Video saved successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsGenerating(true);
    setError('');

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await modelServiceUpload('/upload', formData);

      if (!response.ok) throw new Error('Failed to upload files');

      const data = await response.json();

      const processedFiles = data.files.map(file => {
        const isImage = file.content_type.startsWith('image/');
        return {
          id: Date.now() + Math.random(),
          name: file.filename,
          type: isImage ? 'image' : 'document',
          preview: isImage ? `data:${file.content_type};base64,${file.content}` : null,
          content: file.content,
          contentType: file.content_type,
          size: file.size
        };
      });

      setUploadedFiles(prev => [...prev, ...processedFiles]);
    } catch (err) {
      setError(`Failed to upload files: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReferenceImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (referenceImages.length + files.length > 3) {
      setError('Maximum 3 reference images allowed');
      return;
    }

    setError('');

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await modelServiceUpload('/upload', formData);

      if (!response.ok) throw new Error('Failed to upload reference images');

      const data = await response.json();

      const processedImages = data.files
        .filter(file => file.content_type.startsWith('image/'))
        .map(file => ({
          id: Date.now() + Math.random(),
          name: file.filename,
          preview: `data:${file.content_type};base64,${file.content}`,
          content: file.content,
          contentType: file.content_type,
        }));

      setReferenceImages(prev => [...prev, ...processedImages].slice(0, 3));
    } catch (err) {
      setError(`Failed to upload reference images: ${err.message}`);
    }
  };

  const removeFile = (id) => {
    setUploadedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove?.preview) URL.revokeObjectURL(fileToRemove.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const removeReferenceImage = (id) => {
    setReferenceImages(prev => prev.filter(img => img.id !== id));
  };

  const handleReset = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setCurrentStep('input');
    setScript('');
    setScenes([]);
    setUploadedFiles([]);
    setReferenceImages([]);
    setGeneratedVideo(null);
    setError('');
    setTaskId(null);
    setExtensionProgress(null);
  };

  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    return new Blob([new Uint8Array(byteArrays)], { type: mimeType });
  };

  // ---- Render Steps ----

  const renderInputStep = () => (
    <div className="space-y-6">
      {/* Video Prompt Input */}
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">What video do you want to create?</label>
          <div className="flex gap-2 mt-1 items-end">
            <textarea
              value={topic}
              onChange={(e) => {
                setTopic(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              placeholder="e.g., Explain how LIHTC compliance works"
              rows={1}
              className="flex-1 px-3 py-2 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-hidden"
            />
            <button
              onClick={handleEnhancePrompt}
              disabled={isEnhancing || !topic.trim()}
              title="Enhance prompt for better video generation"
              className="px-3 py-2 bg-gradient-to-r from-orange-500 to-orange-500 text-white rounded-md hover:from-orange-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-xs font-medium"
            >
              {isEnhancing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              Enhance
            </button>
          </div>
        </div>

        {/* Materials Upload */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Materials</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
            >
              <Upload className="w-3 h-3" />
              Add Files
            </button>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="group relative flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs border dark:border-gray-700"
                >
                  {file.type === 'image' ? (
                    <ImageIcon className="w-3 h-3 text-blue-500" />
                  ) : (
                    <FileText className="w-3 h-3 text-gray-500" />
                  )}
                  <span className="max-w-[120px] truncate">{file.name}</span>
                  <button onClick={() => removeFile(file.id)} className="ml-1 p-0.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded">
                    <X className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reference Images (Veo Style Guidance) */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reference Images</label>
              <span className="text-xs text-gray-400">({referenceImages.length}/3)</span>
            </div>
            <input
              ref={refImageInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleReferenceImageUpload}
              className="hidden"
            />
            {referenceImages.length < 3 && (
              <button
                type="button"
                onClick={() => refImageInputRef.current?.click()}
                className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 font-medium"
              >
                <ImageIcon className="w-3 h-3" />
                Add Style Reference
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Guide the visual style with up to 3 reference images</p>

          {referenceImages.length > 0 && (
            <div className="flex gap-2">
              {referenceImages.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.preview}
                    alt={img.name}
                    className="w-16 h-16 object-cover rounded-lg border-2 border-purple-200 dark:border-purple-700"
                  />
                  <button
                    onClick={() => removeReferenceImage(img.id)}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Video Settings Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
          {/* Style */}
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Style</label>
            <select
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
            >
              {VIDEO_STYLES.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Target Duration */}
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Duration (seconds)</label>
            <input
              type="number"
              min={4}
              max={148}
              value={targetDuration}
              onChange={(e) => setTargetDuration(Math.min(148, Math.max(4, Number(e.target.value) || 4)))}
              className="w-full px-2 py-1.5 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
            />
          </div>

          {/* Resolution */}
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Resolution</label>
            <select
              value={effectiveResolution}
              onChange={(e) => setResolution(e.target.value)}
              disabled={maxScenes > 1}
              className="w-full px-2 py-1.5 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50"
            >
              {RESOLUTIONS.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {maxScenes > 1 && (
              <p className="text-xs text-orange-500 mt-0.5">720p for multi-scene</p>
            )}
          </div>

          {/* Aspect Ratio */}
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Aspect</label>
            <div className="flex gap-1">
              <button
                onClick={() => setAspectRatio('16:9')}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs border rounded transition-colors ${
                  aspectRatio === '16:9'
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                    : 'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400'
                }`}
              >
                <Monitor className="w-3 h-3" />
              </button>
              <button
                onClick={() => setAspectRatio('9:16')}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs border rounded transition-colors ${
                  aspectRatio === '9:16'
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                    : 'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400'
                }`}
              >
                <Smartphone className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {/* Auto-computed plan summary */}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 pt-1">
          <Clock className="w-3 h-3" />
          <span>
            {maxScenes} scene{maxScenes > 1 ? 's' : ''} x {durationPerScene}s = <strong>{totalDuration}s</strong>
            {totalDuration !== targetDuration && <span className="text-orange-500 ml-1">(closest to {targetDuration}s)</span>}
            {maxScenes > 1 && ' — continuous video via extension'}
          </span>
        </div>

        {/* RAG toggle */}
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={useRAG}
            onChange={(e) => setUseRAG(e.target.checked)}
            className="w-3 h-3 rounded"
          />
          <span className="text-gray-600 dark:text-gray-400">Use Knowledge Base</span>
        </label>
      </div>

      {/* AI Script Assistant */}
      <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden bg-gradient-to-r from-purple-50/50 to-blue-50/50 dark:from-purple-900/10 dark:to-blue-900/10">
        <div className="px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 flex items-center gap-2 border-b border-purple-100 dark:border-purple-900/50">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200 flex-1">AI Script Assistant</span>
          {chatMessages.length > 0 && (
            <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
              {chatMessages.length} messages
            </span>
          )}
        </div>

        {/* Chat History */}
        {chatMessages.length > 0 && (
          <div className="px-3 py-3 max-h-96 overflow-y-auto space-y-3 bg-white/50 dark:bg-gray-900/30 border-b border-purple-100 dark:border-purple-900/50">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className="flex flex-col gap-2 max-w-[90%]">
                  <div
                    className={`px-3 py-2 rounded-lg text-xs ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200'
                    }`}
                    style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', lineHeight: '1.6' }}
                  >
                    {msg.content.split('\n').map((line, lineIdx) => {
                      const trimmedLine = line.trim();

                      const parseBold = (text) => {
                        let parts = text.split(/(\*\*[^*]+\*\*)/g);
                        return parts.map((part, i) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
                          }
                          const subParts = part.split(/(\*[^*]+\*)/g);
                          return subParts.map((subPart, j) => {
                            if (subPart.startsWith('*') && subPart.endsWith('*') && subPart.length > 2) {
                              return <strong key={`${i}-${j}`} className="font-semibold">{subPart.slice(1, -1)}</strong>;
                            }
                            return subPart;
                          });
                        });
                      };

                      if (trimmedLine.match(/^[\*\-•]\s/)) {
                        const content = trimmedLine.substring(2).trim();
                        return (
                          <div key={lineIdx} className="ml-2 mb-1 flex">
                            <span className="mr-2 flex-shrink-0">&bull;</span>
                            <span>{parseBold(content)}</span>
                          </div>
                        );
                      } else if (trimmedLine.includes('*')) {
                        return <p key={lineIdx} className="mb-1.5">{parseBold(trimmedLine)}</p>;
                      } else if (trimmedLine) {
                        return <p key={lineIdx} className="mb-1.5">{line}</p>;
                      }
                      return <div key={lineIdx} className="h-1" />;
                    })}
                  </div>

                  {msg.role === 'assistant' && msg.content.length > 300 && msg.content.toLowerCase().includes('script') && (
                    <button
                      onClick={() => {
                        setScript(msg.content);
                        setTimeout(() => {
                          document.querySelector('.script-ready-banner')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                      className="self-start px-3 py-1.5 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-md hover:from-green-700 hover:to-blue-700 text-xs font-medium shadow-sm transition-all flex items-center gap-1.5"
                    >
                      <Check className="w-3 h-3" />
                      Use This Script
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Script Ready Indicator */}
        {script && (
          <div className="script-ready-banner px-3 py-2.5 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-b border-green-200 dark:border-green-900/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">Script Generated!</span>
              </div>
              <button
                onClick={() => setScript('')}
                className="text-xs text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:underline"
              >
                Clear
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentStep('script')}
                className="flex-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md hover:from-blue-700 hover:to-purple-700 text-xs font-medium shadow-sm transition-all flex items-center justify-center gap-1.5"
              >
                <Edit3 className="w-3 h-3" />
                View & Edit Script
              </button>
              <button
                onClick={handleQuickGenerateVideo}
                disabled={isGenerating}
                className="flex-1 px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-md hover:from-green-700 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 text-xs font-medium shadow-sm transition-all flex items-center justify-center gap-1.5"
              >
                {isGenerating ? (
                  <><Loader className="w-3 h-3 animate-spin" /> Creating...</>
                ) : (
                  <><Video className="w-3 h-3" /> Generate Video</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-3 bg-white dark:bg-gray-800/50">
          <div className="flex gap-2 items-end">
            <textarea
              value={userPrompt}
              onChange={(e) => {
                setUserPrompt(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAIChat();
                }
              }}
              placeholder="e.g., Add more camera movement, or Make the dialogue more natural"
              rows={1}
              className="flex-1 px-2 py-1.5 text-xs border border-purple-200 dark:border-purple-700 rounded-md dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none overflow-hidden"
            />
            <button
              onClick={handleAIChat}
              disabled={isChatting || !userPrompt.trim()}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed shadow-sm transition-all flex items-center gap-1.5"
            >
              {isChatting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span className="text-xs font-medium">Ask</span>
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {(topic.trim() || script) && (
        <div className="flex items-center justify-center gap-3 pt-4 border-t dark:border-gray-700">
          <button
            onClick={handleQuickGenerateVideo}
            disabled={isGenerating}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium rounded-md hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isGenerating ? (
              <><Loader className="w-4 h-4 animate-spin" /> Creating...</>
            ) : (
              <><Video className="w-4 h-4" /> Generate</>
            )}
          </button>

          {script && (
            <button
              onClick={() => setCurrentStep('script')}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border dark:border-gray-600 rounded-md flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Advanced
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderScriptStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Generated Script</h3>
        <button onClick={handleReset} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">
          Start Over
        </button>
      </div>

      <textarea
        value={script}
        onChange={(e) => setScript(e.target.value)}
        rows={15}
        className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 font-mono text-sm"
      />

      <button
        onClick={handleParseScenes}
        disabled={isGenerating || !script}
        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isGenerating ? (
          <><Loader className="w-5 h-5 animate-spin" /> Parsing...</>
        ) : (
          <><Film className="w-5 h-5" /> Parse into Scenes</>
        )}
      </button>
    </div>
  );

  const renderScenesStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Scenes ({scenes.length})</h3>
        <button onClick={() => setCurrentStep('script')} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">
          Edit Script
        </button>
      </div>

      {/* Settings summary */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">{effectiveResolution}</span>
        <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded">{aspectRatio}</span>
        <span className="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded">{totalDuration}s total</span>
        {scenes.length > 1 && (
          <span className="px-2 py-1 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded">Continuous via extension</span>
        )}
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {scenes.map((scene, index) => (
          <div key={index} className="p-4 border rounded-lg dark:border-gray-600">
            <div className="flex items-start justify-between mb-2">
              <span className="font-semibold">Scene {scene.scene_number}</span>
              <span className="text-sm text-gray-600">{scene.duration}s</span>
            </div>

            <div className="space-y-2 text-sm">
              <div>
                <label className="font-medium text-gray-700 dark:text-gray-300">Visual:</label>
                <textarea
                  value={scene.visual_description}
                  onChange={(e) => handleUpdateScene(index, 'visual_description', e.target.value)}
                  rows={2}
                  className="w-full mt-1 px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="font-medium text-gray-700 dark:text-gray-300">Narration:</label>
                <textarea
                  value={scene.narration}
                  onChange={(e) => handleUpdateScene(index, 'narration', e.target.value)}
                  rows={2}
                  className="w-full mt-1 px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleGenerateVideo}
        disabled={isGenerating || scenes.length === 0}
        className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isGenerating ? (
          <><Loader className="w-5 h-5 animate-spin" /> Generating Video...</>
        ) : (
          <><Video className="w-5 h-5" /> Generate Full Video</>
        )}
      </button>
    </div>
  );

  const renderGeneratingStep = () => {
    const progress = extensionProgress;
    const pct = progress ? Math.round((progress.currentSeconds / progress.targetSeconds) * 100) : 0;

    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-full max-w-md space-y-6">
          {/* Animated icon */}
          <div className="flex justify-center">
            <div className="relative">
              <Loader className="w-16 h-16 animate-spin text-blue-600" />
              <Video className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>

          <h3 className="text-xl font-semibold text-center">Generating Your Video</h3>

          {progress ? (
            <>
              {/* Progress bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${Math.max(pct, 5)}%` }}
                />
              </div>

              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>{progress.currentSeconds}s / {progress.targetSeconds}s</span>
                <span>{pct}%</span>
              </div>

              {/* Scene indicator */}
              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                {progress.status === 'generating' ? (
                  <>Generating initial clip (scene 1 of {progress.totalScenes})...</>
                ) : (
                  <>Extending video — scene {progress.currentScene} of {progress.totalScenes}...</>
                )}
              </p>

              {/* Visual timeline */}
              <div className="flex gap-1">
                {Array.from({ length: progress.totalScenes }, (_, i) => {
                  const sceneNum = i + 1;
                  let colorClass;
                  if (sceneNum < progress.currentScene) {
                    colorClass = 'bg-green-500';
                  } else if (sceneNum === progress.currentScene) {
                    colorClass = 'bg-blue-500 animate-pulse';
                  } else {
                    colorClass = 'bg-gray-300 dark:bg-gray-600';
                  }
                  return (
                    <div key={i} className={`flex-1 h-2 rounded-full ${colorClass} transition-colors`} title={`Scene ${sceneNum}`} />
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-center text-gray-600 dark:text-gray-400">Starting generation...</p>
          )}

          {/* Info box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
            <p className="font-medium mb-1.5">How it works:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>An initial clip is generated first</li>
              {progress && progress.totalScenes > 1 && (
                <li>Each scene seamlessly extends the video</li>
              )}
              <li>Resolution: {progress?.resolution || effectiveResolution}</li>
              <li>The result is a single continuous video — no cuts or joins</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Generated Video</h3>
        <button onClick={handleReset} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">
          Create New
        </button>
      </div>

      {generatedVideo && (
        <>
          <video
            controls
            src={generatedVideo.url}
            className="w-full rounded-lg border dark:border-gray-600"
          />

          <div className="flex gap-3">
            <button
              onClick={handleSaveVideo}
              disabled={isGenerating}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <><Loader className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4" /> Save to Library</>
              )}
            </button>

            <a
              href={generatedVideo.url}
              download="generated_video.mp4"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
          </div>

          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm space-y-1">
            <div className="flex justify-between">
              <span>Duration:</span>
              <span className="font-medium">{generatedVideo.duration}s</span>
            </div>
            <div className="flex justify-between">
              <span>Resolution:</span>
              <span className="font-medium">{generatedVideo.resolution}</span>
            </div>
            <div className="flex justify-between">
              <span>Aspect Ratio:</span>
              <span className="font-medium">{generatedVideo.aspect_ratio}</span>
            </div>
            <div className="flex justify-between">
              <span>Scenes:</span>
              <span className="font-medium">{generatedVideo.scenes_count}</span>
            </div>
            <div className="flex justify-between">
              <span>Size:</span>
              <span className="font-medium">{(generatedVideo.size_bytes / 1024 / 1024).toFixed(2)} MB</span>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex-1">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-2">
            Video Generation
            <div className="relative">
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center hover:from-blue-600 hover:to-purple-600 transition-all"
              >
                <Info className="w-3 h-3 text-white" />
              </button>
              {showInfo && (
                <div className="absolute left-0 top-7 z-50 w-80 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg shadow-xl p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <Film className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <h3 className="text-xs font-semibold text-gray-800 dark:text-gray-200">Veo 3.1 Video Generation</h3>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Multi-scene videos are built as one continuous clip — no cuts or joins. Supports up to 148 seconds, reference images for style guidance, and native dialogue generation.
                  </p>
                  <button onClick={() => setShowInfo(false)} className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                    Got it
                  </button>
                </div>
              )}
            </div>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Create continuous videos — up to 148 seconds
          </p>
        </div>

        <button
          onClick={() => setShowGuide(true)}
          className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded hover:from-purple-700 hover:to-blue-700 shadow-sm transition-all text-xs"
        >
          <BookOpen className="w-3 h-3" />
          Guide
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded text-xs inline-flex items-center gap-1.5">
          <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6">
        {currentStep === 'input' && renderInputStep()}
        {currentStep === 'script' && renderScriptStep()}
        {currentStep === 'scenes' && renderScenesStep()}
        {currentStep === 'generating' && renderGeneratingStep()}
        {currentStep === 'preview' && renderPreviewStep()}
      </div>

      {/* User Guide Modal */}
      <InfoModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Video Generation Guide"
      >
        <div className="space-y-4">
          <section>
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-white">Quick Start</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>Enter your topic or chat with the AI Script Assistant</li>
              <li>Configure style, resolution, aspect ratio, and scene count</li>
              <li>Optionally add reference images to guide the visual style</li>
              <li>Click "Generate" for quick video or "Advanced" to fine-tune scenes</li>
            </ol>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-white">Veo 3.1 Features</h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Video Extension:</strong> Multi-scene videos are built as one continuous clip — no jarring cuts</li>
              <li><strong>Resolution:</strong> 720p, 1080p, or 4K (single scene). Multi-scene requires 720p</li>
              <li><strong>Aspect Ratio:</strong> 16:9 (landscape) or 9:16 (portrait/mobile)</li>
              <li><strong>Reference Images:</strong> Upload up to 3 images to guide the visual style</li>
              <li><strong>Native Audio:</strong> Dialogue, sound effects, and ambient sounds are generated automatically</li>
              <li><strong>Max Duration:</strong> Up to 148 seconds of continuous video</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-white">Prompt Enhancer</h4>
            <p className="mb-2">Click the "Enhance" button next to your prompt to optimize it:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Adds camera movements (dolly, pan, tracking shots)</li>
              <li>Adds lighting and ambiance descriptions</li>
              <li>Formats dialogue for native speech generation</li>
              <li>Adds sound effect descriptions</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-white">Prompt Tips for Best Results</h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Be specific: "A teacher at a whiteboard" vs "Someone teaching"</li>
              <li>Include camera directions: "slow dolly forward", "close-up portrait"</li>
              <li>Describe lighting: "warm golden hour", "cool blue office tones"</li>
              <li>Put dialogue in quotes: The instructor says "Welcome to today's lesson"</li>
              <li>Describe sounds: "gentle background music", "keyboard typing sounds"</li>
              <li>Avoid negation — describe what SHOULD be there, not what shouldn't</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-white">Duration Guide</h4>
            <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs space-y-1">
              <p>1 scene x 8s = 8s (quick clip)</p>
              <p>3 scenes x 8s = 24s (brief explanation)</p>
              <p>5 scenes x 8s = 40s (short tutorial)</p>
              <p>8 scenes x 8s = 64s (full lesson)</p>
              <p>10 scenes x 8s = 80s (detailed tutorial)</p>
              <p>18 scenes x 8s = 144s (near maximum)</p>
            </div>
          </section>

          <section className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
            <p className="text-xs">
              <strong>Timing:</strong> Initial clip takes 1-3 minutes. Each extension takes 30-60 seconds. A 10-scene video takes approximately 8-12 minutes.
            </p>
          </section>
        </div>
      </InfoModal>
    </div>
  );
}

export default VideoGeneration;
